import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { TrendingDown, ArrowRight, Loader2 } from 'lucide-react';
import { Button } from './ui/button';
import axios from 'axios';
import { API } from '../config/api';

export const SavingsCalculator = () => {
  const navigate = useNavigate();
  const [hours, setHours] = useState(4);
  const [loading, setLoading] = useState(true);
  const [pricing, setPricing] = useState({
    ourRate: 25,  // Default fallback
    checkatradeRate: 43.50  // Default fallback (general/handyman rate)
  });
  
  // Fetch pricing from API on mount
  useEffect(() => {
    const fetchPricing = async () => {
      try {
        const response = await axios.get(`${API}/pricing`);
        const data = response.data;
        
        // Use the "general" service rate as the default display rate
        // Add defensive check to ensure hourly_rates is an array
        const hourlyRates = Array.isArray(data?.hourly_rates) ? data.hourly_rates : [];
        const generalRate = hourlyRates.find(r => r.service_id === 'general');
        if (generalRate) {
          setPricing({
            ourRate: generalRate.our_rate || 35,
            checkatradeRate: generalRate.checkatrade_rate || 48
          });
        }
      } catch (error) {
        console.error('Failed to fetch pricing:', error);
        // Keep default values on error
      } finally {
        setLoading(false);
      }
    };
    
    fetchPricing();
  }, []);
  
  const ourPrice = hours * pricing.ourRate;
  const checkatradePrice = hours * pricing.checkatradeRate;
  const savings = checkatradePrice - ourPrice;
  const savingsPercent = Math.round((savings / checkatradePrice) * 1000) / 10;

  return (
    <div className="bg-gradient-to-br from-primary/5 to-primary/10 rounded-2xl p-6">
      <div className="text-center mb-6">
        <h3 className="text-xl font-bold font-['Outfit'] mb-1">See Your Savings</h3>
        <p className="text-sm text-muted-foreground">Drag to see how much you&apos;ll save</p>
      </div>

      {loading ? (
        <div className="flex justify-center items-center py-8">
          <Loader2 className="w-6 h-6 animate-spin text-primary" />
        </div>
      ) : (
        <>
          {/* Slider */}
          <div className="mb-6">
            <div className="flex justify-between text-sm mb-2">
              <span className="text-muted-foreground">Hours of work</span>
              <span className="font-bold text-primary">{hours} hours</span>
            </div>
            <input
              type="range"
              min="1"
              max="8"
              value={hours}
              onChange={(e) => setHours(parseInt(e.target.value))}
              className="w-full h-3 bg-gray-200 rounded-full appearance-none cursor-pointer accent-primary"
              style={{
                background: `linear-gradient(to right, #84cc16 0%, #84cc16 ${(hours - 1) / 7 * 100}%, #e5e7eb ${(hours - 1) / 7 * 100}%, #e5e7eb 100%)`
              }}
              data-testid="savings-slider"
            />
            <div className="flex justify-between text-xs text-muted-foreground mt-1">
              <span>1 hr</span>
              <span>8 hrs</span>
            </div>
          </div>

          {/* Price Comparison */}
          <div className="grid grid-cols-2 gap-4 mb-4">
            <div className="bg-white rounded-xl p-4 text-center">
              <p className="text-xs text-muted-foreground mb-1">Checkatrade</p>
              <p className="text-2xl font-bold text-gray-400 line-through" data-testid="checkatrade-price">£{checkatradePrice}</p>
            </div>
            <div className="bg-primary rounded-xl p-4 text-center text-white">
              <p className="text-xs opacity-80 mb-1">Our Price</p>
              <p className="text-2xl font-bold" data-testid="our-price">£{ourPrice}</p>
            </div>
          </div>

          {/* Savings Badge */}
          <div className="bg-green-500 text-white rounded-xl p-4 flex items-center justify-center gap-3">
            <TrendingDown className="w-6 h-6" />
            <div>
              <p className="text-sm opacity-90">You Save</p>
              <p className="text-2xl font-bold" data-testid="savings-amount">£{savings} <span className="text-base font-normal">({savingsPercent}%)</span></p>
            </div>
          </div>
        </>
      )}

      <Button 
        onClick={() => navigate('/quote')}
        className="w-full mt-4 btn-accent"
        data-testid="get-quote-btn"
      >
        Get Your Quote <ArrowRight className="ml-2 w-4 h-4" />
      </Button>
    </div>
  );
};
